/**
 * Onboarding Store for Phishiness Frontend (T046)
 *
 * Manages state for the customer onboarding tour:
 * - Current step and progress tracking
 * - Step data storage
 * - Navigation between steps
 * - Completion and cancellation
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { onboardingService } from '@/services/onboarding';
import type {
  OnboardingStep,
  OnboardingStepStatus,
  OnboardingStepProgress,
  OnboardingStepUpdateRequest,
  CompanyInfoStepData,
  DomainSetupStepData,
  BrandKeywordsStepData,
  DmarcSetupStepData,
} from '@/types/onboarding';

/** Step order for navigation */
const STEP_ORDER: OnboardingStep[] = [
  'company_info',
  'domain_setup',
  'brand_keywords',
  'dmarc_setup',
];

interface OnboardingState {
  // Progress state
  companyId: string | null;
  currentStep: OnboardingStep;
  isComplete: boolean;
  steps: OnboardingStepProgress[];
  startedAt: string | null;
  completedAt: string | null;
  canSkip: boolean;

  // Step data (locally cached)
  stepData: {
    company_info: CompanyInfoStepData;
    domain_setup: DomainSetupStepData;
    brand_keywords: BrandKeywordsStepData;
    dmarc_setup: DmarcSetupStepData;
  };

  // UI state
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  hasLoadedInitialData: boolean;

  // Actions - Progress management
  loadProgress: () => Promise<void>;
  updateStep: (step: OnboardingStep, data: OnboardingStepUpdateRequest) => Promise<boolean>;
  skipStep: (step: OnboardingStep) => Promise<boolean>;
  completeOnboarding: () => Promise<{ success: boolean; redirectTo?: string }>;
  cancelOnboarding: () => Promise<boolean>;
  resumeOnboarding: () => Promise<boolean>;

  // Actions - Local state
  setCurrentStep: (step: OnboardingStep) => void;
  setStepData: <T extends OnboardingStep>(
    step: T,
    data: Partial<OnboardingState['stepData'][T]>
  ) => void;
  clearError: () => void;
  reset: () => void;

  // Computed helpers
  getStepIndex: (step: OnboardingStep) => number;
  getStepStatus: (step: OnboardingStep) => OnboardingStepStatus;
  canNavigateToStep: (step: OnboardingStep) => boolean;
  isStepCompleted: (step: OnboardingStep) => boolean;
  getNextStep: () => OnboardingStep | null;
  getPreviousStep: () => OnboardingStep | null;
  getCompletedStepsCount: () => number;
  getProgressPercentage: () => number;
}

const initialStepData = {
  company_info: {},
  domain_setup: { additional_domains: [] },
  brand_keywords: { brand_names: [], additional_keywords: [] },
  dmarc_setup: { setup_instructions_viewed: false, dmarc_record_verified: false },
};

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      // Initial state
      companyId: null,
      currentStep: 'company_info',
      isComplete: false,
      steps: [],
      startedAt: null,
      completedAt: null,
      canSkip: true,

      stepData: { ...initialStepData },

      isLoading: false,
      isSaving: false,
      error: null,
      hasLoadedInitialData: false,

      // Actions - Progress management
      loadProgress: async () => {
        set({ isLoading: true, error: null });
        try {
          const progress = await onboardingService.getProgress();

          // Extract step data from progress
          const stepData = { ...initialStepData };
          for (const step of progress.steps) {
            if (step.data) {
              stepData[step.step] = { ...stepData[step.step], ...step.data } as never;
            }
          }

          set({
            companyId: progress.company_id,
            currentStep: progress.current_step,
            isComplete: progress.is_complete,
            steps: progress.steps,
            startedAt: progress.started_at,
            completedAt: progress.completed_at ?? null,
            canSkip: progress.can_skip,
            stepData,
            isLoading: false,
            hasLoadedInitialData: true,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to load onboarding progress';
          set({ isLoading: false, error: errorMessage });
          throw error;
        }
      },

      updateStep: async (step, data) => {
        set({ isSaving: true, error: null });
        try {
          const response = await onboardingService.updateStep(step, data);

          if (response.success) {
            // Update local step data
            set((state) => ({
              stepData: {
                ...state.stepData,
                [step]: { ...state.stepData[step], ...data },
              },
              currentStep: response.next_step ?? state.currentStep,
              isSaving: false,
            }));

            // Reload full progress to sync with server
            await get().loadProgress();
            return true;
          } else {
            set({ isSaving: false, error: response.message ?? 'Failed to update step' });
            return false;
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update step';
          set({ isSaving: false, error: errorMessage });
          return false;
        }
      },

      skipStep: async (step) => {
        set({ isSaving: true, error: null });
        try {
          const response = await onboardingService.skipStep(step);

          if (response.success) {
            set((state) => ({
              currentStep: response.next_step ?? state.currentStep,
              isSaving: false,
            }));

            // Reload full progress to sync with server
            await get().loadProgress();
            return true;
          } else {
            set({ isSaving: false, error: response.message ?? 'Failed to skip step' });
            return false;
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to skip step';
          set({ isSaving: false, error: errorMessage });
          return false;
        }
      },

      completeOnboarding: async () => {
        set({ isSaving: true, error: null });
        try {
          const response = await onboardingService.complete();

          if (response.success) {
            set({
              isComplete: true,
              completedAt: new Date().toISOString(),
              isSaving: false,
            });
            return { success: true, redirectTo: response.redirect_to };
          } else {
            set({ isSaving: false, error: response.message ?? 'Failed to complete onboarding' });
            return { success: false };
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to complete onboarding';
          set({ isSaving: false, error: errorMessage });
          return { success: false };
        }
      },

      cancelOnboarding: async () => {
        set({ isSaving: true, error: null });
        try {
          const response = await onboardingService.cancel();

          if (response.success) {
            set({ isSaving: false });
            return true;
          } else {
            set({ isSaving: false, error: response.message ?? 'Failed to cancel onboarding' });
            return false;
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to cancel onboarding';
          set({ isSaving: false, error: errorMessage });
          return false;
        }
      },

      resumeOnboarding: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await onboardingService.resume();

          if (response.success) {
            set({
              currentStep: response.current_step,
              isLoading: false,
            });

            // Reload full progress
            await get().loadProgress();
            return true;
          } else {
            set({ isLoading: false, error: response.message ?? 'Failed to resume onboarding' });
            return false;
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to resume onboarding';
          set({ isLoading: false, error: errorMessage });
          return false;
        }
      },

      // Actions - Local state
      setCurrentStep: (step) => {
        set({ currentStep: step });
      },

      setStepData: (step, data) => {
        set((state) => ({
          stepData: {
            ...state.stepData,
            [step]: { ...state.stepData[step], ...data },
          },
        }));
      },

      clearError: () => {
        set({ error: null });
      },

      reset: () => {
        set({
          companyId: null,
          currentStep: 'company_info',
          isComplete: false,
          steps: [],
          startedAt: null,
          completedAt: null,
          canSkip: true,
          stepData: { ...initialStepData },
          isLoading: false,
          isSaving: false,
          error: null,
          hasLoadedInitialData: false,
        });
      },

      // Computed helpers
      getStepIndex: (step) => {
        return STEP_ORDER.indexOf(step);
      },

      getStepStatus: (step) => {
        const { steps, currentStep } = get();
        const stepProgress = steps.find((s) => s.step === step);
        if (stepProgress) {
          return stepProgress.status;
        }

        const stepIndex = STEP_ORDER.indexOf(step);
        const currentIndex = STEP_ORDER.indexOf(currentStep);

        if (stepIndex < currentIndex) return 'completed';
        if (stepIndex === currentIndex) return 'in_progress';
        return 'pending';
      },

      canNavigateToStep: (step) => {
        const { currentStep } = get();
        const stepIndex = STEP_ORDER.indexOf(step);
        const currentIndex = STEP_ORDER.indexOf(currentStep);

        // Can navigate to current step or any completed step
        return stepIndex <= currentIndex;
      },

      isStepCompleted: (step) => {
        const status = get().getStepStatus(step);
        return status === 'completed' || status === 'skipped';
      },

      getNextStep: (): OnboardingStep | null => {
        const { currentStep } = get();
        const currentIndex = STEP_ORDER.indexOf(currentStep);
        if (currentIndex >= 0 && currentIndex < STEP_ORDER.length - 1) {
          return STEP_ORDER[currentIndex + 1] ?? null;
        }
        return null;
      },

      getPreviousStep: (): OnboardingStep | null => {
        const { currentStep } = get();
        const currentIndex = STEP_ORDER.indexOf(currentStep);
        if (currentIndex > 0) {
          return STEP_ORDER[currentIndex - 1] ?? null;
        }
        return null;
      },

      getCompletedStepsCount: () => {
        const { steps } = get();
        return steps.filter((s) => s.status === 'completed' || s.status === 'skipped').length;
      },

      getProgressPercentage: () => {
        const completedCount = get().getCompletedStepsCount();
        return Math.round((completedCount / STEP_ORDER.length) * 100);
      },
    }),
    {
      name: 'phishiness-onboarding',
      partialize: (state) => ({
        // Only persist step data for form recovery
        // Do NOT persist currentStep or hasLoadedInitialData - these must come from server
        // to prevent showing wrong step when different user logs in
        stepData: state.stepData,
      }),
    }
  )
);

export default useOnboardingStore;
